/*

& Broadcast and Boost on the owner's listing page (3 Nov 2026, gap A2), as the app's listing screen has them in its
& bottom bar: "Broadcast" (or "Broadcast again", or "Broadcast in: …" while the 24-hour window is closed) and "Boost now"
& (or "Active boost" while one runs), for a published listing. They open what the dashboard's listing cards open: the
& Create Broadcast flow and the AI Boost request form (switched to the new dashboard's flow when it goes live, #11).

  Who sees it: properties/cards/property-stats answers only the listing's team, so this asks it and shows nothing when
  it refuses. Never in "Preview as buyer" (a buyer never sees it).

*/

import { styled, useMediaQuery, useTheme } from "@mui/system";
import { useRouter } from "next/router";
import React from "react";
import { useSelector } from "react-redux";

import { Typography } from "@mui/material";
import StateInterface from "../../../../../redux-magic/state-interface";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import AIBoostFormOfflineModal from "../../../../modals/AiBoostFormOfflineModal";
import AiBoostFormOfflineMobileModal from "../../../../modals/AiBoostFormOfflineMobileModal";
import CreateNewBroadcastModal from "../../../Broadcasts/CreateNewBroadcastModal";

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	gap: "0.75rem",
	padding: "1rem",
	background: theme.palette.background.paper,
	borderRadius: "16px",
}));

type Stats = { is_recently_broadcasted: boolean; is_broadcasted_before: boolean; time_left_to_broadcast_again: number };

const ListingPromoteActions = ({
	propertyData,
	businessProfileId,
	dispatch,
	session,
	profile_context,
}: {
	propertyData: any;
	businessProfileId: string;
	dispatch: Function;
	session: any;
	profile_context: StateInterface["profile_context"];
}) => {
	const router = useRouter();
	const theme = useTheme();
	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));
	/* Arabic and Kannada beside English, as the screens around this one write them (router.locale). */
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;

	const broadcastData = useSelector((state: { globalState: StateInterface }) => state.globalState?.broadcast_data);
	const broadcastTracker = useSelector(
		(state: { globalState: StateInterface }) => state.globalState?.feature_discovery_tracker?.broadcast,
	);

	const [stats, setStats] = React.useState<Stats | null>(null);
	const [broadcastOpen, setBroadcastOpen] = React.useState<boolean>(false);
	const [boostOpen, setBoostOpen] = React.useState<boolean>(false);

	const propertyId: string = String(propertyData?._id || "");
	const published: boolean = propertyData?.published === true && propertyData?.status === "published";

	React.useEffect(() => {
		setStats(null);
		if (!propertyId || !published) return;
		let active = true;
		fetch(`${process.env.PRODUCTION_API_URL}properties/cards/property-stats`, {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "text/plain" },
			body: JSON.stringify({ _id: propertyId }),
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((data) => {
				if (active && data?.status === "success") setStats(data.payload);
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [propertyId, published]);

	if (!stats || !published || router.query.preview === "buyer") return null;

	const boosted: boolean = propertyData?.boost_information?.active === true;
	const hoursLeft: number = Math.max(1, Math.ceil((stats.time_left_to_broadcast_again || 0) / 3600000));
	const broadcastLabel: string = stats.is_recently_broadcasted
		? tr(`Broadcast in: ${hoursLeft}h`, `يمكنك البث بعد: ${hoursLeft} س`, `ಪ್ರಸಾರ ಸಮಯ: ${hoursLeft}ಗಂ`)
		: stats.is_broadcasted_before
			? tr("Broadcast again", "بث مرة أخرى", "ಮರು ಪ್ರಸಾರ ಮಾಡಿ")
			: tr("Broadcast", "بث", "ಪ್ರಸಾರ");

	return (
		<MainContainer>
			<Typography sx={{ fontSize: { xs: "1rem", md1190: "1.125rem" }, fontWeight: 500 }}>
				{tr("Promote this listing", "روّج لهذا العقار", "ಈ ಪಟ್ಟಿಯನ್ನು ಪ್ರಚಾರ ಮಾಡಿ")}
			</Typography>
			<div style={{ display: "flex", gap: "0.75rem" }}>
				<BeegruButton
					variant="outlined"
					flavor="primary"
					size="medium"
					fullWidth
					disabled={stats.is_recently_broadcasted}
					onClick={() => setBroadcastOpen(true)}
				>
					{broadcastLabel}
				</BeegruButton>
				<BeegruButton
					variant="contained"
					flavor="boost"
					size="medium"
					fullWidth
					disabled={boosted}
					onClick={() => setBoostOpen(true)}
				>
					{boosted ? tr("Active boost", "تعزيز نشط", "ಸಕ್ರಿಯ ಬೂಸ್ಟ್") : tr("Boost now", "تعزيز الآن", "ಈಗ ಬೂಸ್ಟ್ ಮಾಡಿ")}
				</BeegruButton>
			</div>

			<CreateNewBroadcastModal
				source={{ url: String(propertyData?.slug || ""), component: "listing_page" }}
				router={router}
				setOpen={setBroadcastOpen}
				open={broadcastOpen}
				propertyStatus={String(propertyData?.status || "")}
				session={session}
				profile_context={profile_context}
				business_profile_id={businessProfileId}
				dispatch={dispatch}
				broadcast={broadcastData}
				transactionType={String(propertyData?.transaction_type || "")}
				propertyId={propertyId}
				broadcastFeatureDiscoveryTracker={broadcastTracker as StateInterface["feature_discovery_tracker"]["broadcast"]}
			/>

			{smDevice ? (
				<AiBoostFormOfflineMobileModal
					source={{ url: String(propertyData?.slug || ""), component: "listing_page", form_type: "aiboost_mobile_offlineform" }}
					open={boostOpen}
					handleClose={() => setBoostOpen(false)}
					dispatch={dispatch}
					propertyId={propertyId}
				/>
			) : (
				<AIBoostFormOfflineModal
					source={{ url: String(propertyData?.slug || ""), component: "listing_page", form_type: "aiboost_offlineform" }}
					open={boostOpen}
					handleClose={() => setBoostOpen(false)}
					dispatch={dispatch}
					propertyId={propertyId}
				/>
			)}
		</MainContainer>
	);
};

export default ListingPromoteActions;
